import { useEffect, useRef, useCallback } from 'react';
import { useAtom } from 'jotai';
import { contactsScrollPositionAtom } from '@/lib/atoms';

/**
 * Saves and restores virtual scroll position across navigations using a Jotai atom.
 *
 * - Continuously saves scroll position on every scroll event (not just unmount,
 *   because React may destroy the DOM before cleanup effects run).
 * - On mount: restores saved scrollTop from the atom after data renders.
 */
export function useVirtualScrollPosition(
  scrollElementRef: React.RefObject<HTMLDivElement>,
) {
  const [savedPosition, setSavedPosition] = useAtom(contactsScrollPositionAtom);
  const hasRestored = useRef(false);

  // Reset hasRestored on each mount so we attempt restoration each time
  useEffect(() => {
    hasRestored.current = false;
  }, []);

  // Save scroll position continuously on scroll events
  // This is the key fix: unmount cleanup can't reliably access the DOM element
  // because React may destroy it before running effects. Saving on scroll ensures
  // we always have the latest position.
  const handleScroll = useCallback(() => {
    const el = scrollElementRef.current;
    if (el && el.scrollTop > 0 && hasRestored.current) {
      // Only save after initial restoration is complete (avoid saving 0 during mount)
      setSavedPosition(Math.round(el.scrollTop));
    }
  }, [scrollElementRef, setSavedPosition]);

  useEffect(() => {
    const el = scrollElementRef.current;
    if (!el) return;

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [scrollElementRef, handleScroll]);

  // Restore scroll position on mount (after data renders)
  useEffect(() => {
    console.log('[ScrollRestore] Restore effect. savedPosition:', savedPosition, 'hasRestored:', hasRestored.current);

    if (hasRestored.current || savedPosition <= 0) {
      // Even if nothing to restore, mark as restored so scroll saving begins
      if (savedPosition <= 0) {
        hasRestored.current = true;
      }
      return;
    }

    // Wait for content to render, then try to restore
    const attempts = [50, 100, 200, 400, 800, 1500];
    const timeouts: number[] = [];

    for (const delay of attempts) {
      timeouts.push(window.setTimeout(() => {
        const el = scrollElementRef.current;
        if (!el || hasRestored.current) return;

        const maxScroll = el.scrollHeight - el.clientHeight;
        console.log(`[ScrollRestore] Attempt at ${delay}ms — maxScroll: ${maxScroll}, target: ${savedPosition}`);

        if (maxScroll > 0) {
          const targetScroll = Math.min(savedPosition, maxScroll);
          el.scrollTop = targetScroll;
          console.log(`[ScrollRestore] Set scrollTop to ${targetScroll}, actual: ${el.scrollTop}`);
          hasRestored.current = true;
        }
      }, delay));
    }

    return () => timeouts.forEach(clearTimeout);
  }, [savedPosition, scrollElementRef]);
}
